import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CustomerShell } from "@/components/CustomerShell";
import { SERVICE_RECOMMENDATIONS, TIME_WINDOWS, nextDays, rupee } from "@/lib/constants";
import { windowPast, firstFutureWindow } from "@/lib/time";
import { toast } from "sonner";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { getActiveHomeId } from "@/components/LocationSheet";

function VisitFeeGstNote({ cart }) {
    const g = cart.reduce((a, c) => { const x = c.service.visit_fee_gst || {}; return { taxable: a.taxable + (x.taxable || 0), gst: a.gst + (x.gst || 0), rate: x.rate || a.rate }; }, { taxable: 0, gst: 0, rate: 0 });
    return (
        <div className="mt-2 rounded-xl bg-navy/[0.03] p-2.5 text-[11px] text-navy/60" data-testid="visit-fee-gst">
            <div className="flex justify-between"><span>Taxable value</span><span>{rupee(g.taxable.toFixed(2))}</span></div>
            <div className="flex justify-between"><span>GST @ {g.rate}% (CGST {g.rate / 2}% + SGST {g.rate / 2}%)</span><span>{rupee(g.gst.toFixed(2))}</span></div>
            <p className="mt-1 font-semibold text-emerald-700">Visit fee is GST-inclusive and is fully adjusted in your final bill — never charged twice.</p>
        </div>
    );
}
import { ChevronLeft, Check, Loader2, CheckCircle2, ShieldCheck, Plus, Trash2, X, BellRing, Clock, Sparkles, Users, Wallet } from "lucide-react";

const URGENCY = ["As soon as available", "Within 7 days", "Within 30 days", "Just exploring"];

import { loadRazorpay, openRazorpay } from "@/lib/razorpay";
import { ProblemPicker } from "@/components/ProblemPicker";

export default function BookingFlow() {
    const [params] = useSearchParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const days = nextDays(7);
    const [services, setServices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [areaId, setAreaId] = useState("");
    const [homes, setHomes] = useState([]);
    const [homeId, setHomeId] = useState("");
    const [step, setStep] = useState(1);
    const [cart, setCart] = useState([]); // [{service, problem}]
    const [picking, setPicking] = useState(null); // service to pick problem
    const [demanding, setDemanding] = useState(null); // service to request
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null);
    const [consent, setConsent] = useState(false);
    const [policyVersion, setPolicyVersion] = useState("");
    const [details, setDetails] = useState({
        name: user?.name || "", phone: user?.phone || "", address_line: "", society: "",
        preferred_date: days[0].value, preferred_window: TIME_WINDOWS[1], referral_code: "",
    });
    const setD = (k, v) => setDetails((s) => ({ ...s, [k]: v }));
    const [society, setSociety] = useState(null);

    useEffect(() => {
        const code = params.get("society") || localStorage.getItem("homiie_society");
        if (!code) { return; }
        localStorage.setItem("homiie_society", code);
        api.get(`/public/society/${code}`).then((r) => {
            setSociety(r.data);
            setDetails((s) => ({ ...s, society: s.society || r.data.name }));
            if (r.data.area_id) { setAreaId(r.data.area_id); }
        }).catch(() => localStorage.removeItem("homiie_society"));
    }, [params]);

    useEffect(() => {
        const isToday = details.preferred_date === days[0].value;
        if (windowPast(details.preferred_window, isToday)) {
            setD("preferred_window", firstFutureWindow(TIME_WINDOWS, isToday));
        }
    }, [details.preferred_date]); // eslint-disable-line

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data)).catch(() => {});
        api.get("/service-areas").then((r) => { setAreas(r.data); if (r.data[0]) setAreaId((cur) => cur || r.data[0].id); }).catch(() => {});
        api.get("/homes").then((r) => setHomes(r.data)).catch(() => {});
        api.get("/legal/config").then((r) => setPolicyVersion(r.data.current_version)).catch(() => {});
    }, []);
    useLiveRefresh(() => {
        api.get("/services").then((r) => setServices(r.data)).catch(() => {});
        api.get("/service-areas").then((r) => {
            setAreas(r.data);
            if (areaId && !r.data.some((a) => a.id === areaId)) {
                setAreaId(r.data[0]?.id || "");
                setStep(1);
                toast.error("Services are no longer available in the selected area. Please choose another area.");
            }
        }).catch(() => {});
    });

    const homeParam = params.get("home");
    useEffect(() => {
        if (homeParam && homes.length && !homeId) applyHome(homes.find((h) => h.id === homeParam));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeParam, homes]);

    // Auto-pick the customer's registered (active / first) address when it's in a serviceable area
    const [blockedHome, setBlockedHome] = useState(null);
    const autoPicked = useRef(false);
    useEffect(() => {
        if (autoPicked.current || homeParam || homeId || !homes.length || !areas.length) return;
        autoPicked.current = true;
        const h = homes.find((x) => x.id === getActiveHomeId()) || homes[0];
        if (areas.some((a) => a.id === h.area_id)) { applyHome(h); setBlockedHome(null); }
        else setBlockedHome(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homes, areas]);

    const selectedHome = homes.find((h) => h.id === homeId);
    const applyHome = (home) => {
        if (!home) return;
        setHomeId(home.id);
        if (home.area_id) setAreaId(home.area_id);
        setDetails((s) => ({ ...s, address_line: home.address_line || "", society: society?.name || home.society || "", name: user?.name || s.name, phone: user?.phone || s.phone }));
        setStep((st) => (st === 1 ? 2 : st));
    };
    const clearHome = () => setHomeId("");

    const preselect = params.get("service");
    useEffect(() => {
        if (preselect && services.length && areaId) {
            const s = services.find((x) => x.id === preselect);
            if (s && (s.active_area_ids || []).includes(areaId)) { setPicking(s); setStep(2); }
        }
    }, [preselect, services, areaId]);

    const available = services.filter((s) => (s.active_area_ids || []).includes(areaId));
    const coming = services.filter((s) => !(s.active_area_ids || []).includes(areaId));
    const inCart = (id) => cart.some((c) => c.service.id === id);
    const subtotal = cart.reduce((a, c) => a + c.service.base_price, 0);
    const discount = cart.length >= 3 ? 100 : cart.length === 2 ? 50 : 0;
    const visitFeeTotal = cart.reduce((a, c) => a + (c.service.visit_fee || 0), 0);

    const recommended = (() => {
        if (!cart.length) return [];
        const want = new Set();
        cart.forEach((c) => (SERVICE_RECOMMENDATIONS[c.service.category] || []).forEach((cat) => want.add(cat)));
        return available.filter((s) => want.has(s.category) && !inCart(s.id)).slice(0, 4);
    })();

    const addToCart = (service, problems) => {
        const uniqP = [...new Set(problems.map((p) => p.trim()).filter(Boolean))];
        if (!uniqP.length) return toast.error("Select at least one problem");
        setCart((c) => {
            const entry = { service, problems: uniqP, problem: uniqP.join(", ") };
            return c.some((x) => x.service.id === service.id) ? c.map((x) => (x.service.id === service.id ? entry : x)) : [...c, entry];
        });
        setPicking(null);
        toast.success(`${service.name.replace(" Visit", "")} · ${uniqP.length} problem${uniqP.length > 1 ? "s" : ""} — one visit fee`);
    };

    const requestDemand = async (service, urgency) => {
        try {
            const d = await api.post("/demands", { service_id: service.id, area_id: areaId, society: details.society || null, urgency });
            toast.success(`Request recorded (${d.data.demand_id})`);
            setDemanding(null);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    const submit = async () => {
        if (!details.name || !details.phone || !details.address_line) return toast.error("Fill name, phone & address");
        if (!consent) return toast.error("Please accept the policies to continue");
        setBusy(true);
        try {
            const { data } = await api.post("/bookings/multi", {
                items: cart.map((c) => ({ service_id: c.service.id, problem: c.problem })),
                name: details.name, phone: details.phone, address_line: details.address_line,
                area_id: areaId, society: details.society, preferred_date: details.preferred_date,
                preferred_window: details.preferred_window, referral_code: details.referral_code,
                home_id: homeId || undefined,
                consent_accepted: consent, policy_version: policyVersion || undefined,
                society_code: society?.code,
            });
            setCreated(data); setStep(5); localStorage.removeItem("homiie_society");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const bookingPayload = () => ({
        items: cart.map((c) => ({ service_id: c.service.id, problem: c.problem })),
        name: details.name, phone: details.phone, address_line: details.address_line,
        area_id: areaId, society: details.society, preferred_date: details.preferred_date,
        preferred_window: details.preferred_window, referral_code: details.referral_code,
        home_id: homeId || undefined,
        consent_accepted: consent, policy_version: policyVersion || undefined,
        society_code: society?.code,
    });

    const payAndBook = async () => {
        if (!details.name || !details.phone || !details.address_line) return toast.error("Fill name, phone & address");
        if (!consent) return toast.error("Please accept the policies to continue");
        if (visitFeeTotal <= 0) return submit();
        setBusy(true);
        try {
            const { data: order } = await api.post("/payments/razorpay/order", bookingPayload());
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment gateway. Check your connection."); setBusy(false); return; }
            openRazorpay(order, {
                onSuccess: async (res) => {
                    try {
                        const { data } = await api.post("/payments/razorpay/verify", {
                            pending_id: order.pending_id,
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        setCreated(data); setStep(5); localStorage.removeItem("homiie_society");
                    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Payment verification failed"); }
                    finally { setBusy(false); }
                },
                onDismiss: () => { setBusy(false); toast("Payment cancelled — your booking is not placed yet."); },
                onError: () => { setBusy(false); toast.error("Payment failed. Please try again."); },
            });
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
    };

    const areaName = areas.find((a) => a.id === areaId)?.name;

    const header = (title, sub) => (
        <div className="sticky top-0 z-20 bg-navy px-5 pb-4 pt-5 text-white">
            <div className="flex items-center gap-3">
                <button data-testid="cart-back-btn" onClick={() => (step > 1 && step < 5 ? setStep(step - 1) : nav(-1))} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><ChevronLeft size={20} /></button>
                <div><p className="text-xs text-white/50">{sub}</p><h1 className="font-heading text-lg font-bold">{title}</h1></div>
            </div>
        </div>
    );

    if (step === 5 && created) {
        return (
            <CustomerShell noNav>
                <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
                    <div className="rounded-full bg-emerald-50 p-5"><CheckCircle2 size={56} className="text-emerald-500" /></div>
                    <h1 className="mt-5 font-heading text-2xl font-extrabold text-navy">Booking Confirmed!</h1>
                    <p className="mt-1 text-sm text-navy/55">{created.children.length} service{created.children.length > 1 ? "s" : ""} in one HOMIIE booking.</p>
                    {created.visit_fee_total > 0 && <p className="mt-0.5 text-xs font-semibold text-emerald-600">Visit fee {rupee(created.visit_fee_total)} paid ✓</p>}
                    {selectedHome && <p className="text-xs font-semibold text-orange-dark">{selectedHome.label} · {selectedHome.home_id}</p>}
                    <div data-testid="waiting-assignment-badge" className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700">
                        <Clock size={15} /> Waiting for Professional Assignment
                    </div>
                    <div className="mt-6 w-full rounded-2xl border border-navy/10 bg-white p-5 text-left">
                        <div className="flex items-center justify-between"><span className="text-xs text-navy/50">Booking ID</span><span data-testid="master-booking-id" className="font-heading text-lg font-extrabold text-orange">{created.booking_id}</span></div>
                        <div className="mt-3 space-y-2 border-t border-navy/10 pt-3">
                            {created.children.map((c) => (
                                <div key={c.id} className="flex justify-between text-sm"><span className="text-navy/70">{c.service_name}</span><span className="font-medium text-navy">{c.booking_id}</span></div>
                            ))}
                        </div>
                    </div>
                    <button data-testid="track-master-btn" onClick={() => nav(`/bookings/master/${created.id}`)} className="mt-6 w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white active:scale-95">TRACK BOOKING</button>
                    <button onClick={() => nav("/")} className="mt-3 text-sm font-semibold text-navy/60">Back to Home</button>
                </div>
            </CustomerShell>
        );
    }

    return (
        <CustomerShell noNav>
            {header(step === 1 ? "Where do you need service?" : step === 2 ? "Build your service list" : step === 3 ? "Your details" : "Pay visit fee", `Step ${step} of 4`)}
            <div className={`px-5 py-5 ${step === 2 && cart.length > 0 ? "pb-60" : "pb-40"}`}>
                {step >= 2 && homes.length > 0 && (
                    <div data-testid="booking-for-bar" className="mb-4 rounded-2xl border border-navy/10 bg-white p-3">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-navy/40">Booking for</p>
                        <select data-testid="change-home-select" value={homeId} onChange={(e) => (e.target.value === "__new" ? (clearHome(), nav("/my-home")) : e.target.value ? applyHome(homes.find((h) => h.id === e.target.value)) : clearHome())} className="w-full rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm font-semibold text-navy outline-none focus:border-orange">
                            <option value="">New address (enter below)</option>
                            {homes.map((h) => <option key={h.id} value={h.id}>{h.label} · {h.home_id}</option>)}
                            <option value="__new">+ Add a new home</option>
                        </select>
                        {selectedHome && <p className="mt-1.5 text-xs text-navy/55">{selectedHome.address_line}{selectedHome.society ? `, ${selectedHome.society}` : ""}</p>}
                        {selectedHome && !areas.some((a) => a.id === selectedHome.area_id) && <p data-testid="home-not-serviceable" className="mt-1.5 rounded-lg bg-amber-50 p-2 text-xs font-semibold text-amber-700">Services aren't available at this address's area yet. Please pick another address or area.</p>}
                    </div>
                )}
                {society && <div data-testid="society-booking-banner" className="rounded-2xl border border-teal/25 bg-aqua-light/50 p-3 text-xs text-teal-dark"><p className="font-bold">Booking for {society.name} residents</p>{society.deal_discount && <p className="mt-0.5">Society offer: {society.deal_discount} (applied by the HOMIIE team on your bill)</p>}</div>}
                {step === 1 && blockedHome && (
                    <div data-testid="registered-home-blocked" className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        <b>{blockedHome.label}</b> ({blockedHome.address_line}) is outside our current service areas. Choose an available area below or add another address. HOMIIE Care is still available across Jodhpur.
                    </div>
                )}
                {step === 1 && (
                    <div className="space-y-2.5">
                        {areas.map((a) => (
                            <button key={a.id} data-testid={`area-${a.name.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => { setAreaId(a.id); setStep(2); }} className={`flex w-full items-center justify-between rounded-2xl border bg-white px-4 py-4 text-left ${areaId === a.id ? "border-orange ring-2 ring-orange/30" : "border-navy/10"}`}>
                                <span className="font-heading font-bold text-navy">{a.name}</span>
                                {areaId === a.id && <Check size={18} className="text-orange" />}
                            </button>
                        ))}
                    </div>
                )}

                {step === 2 && (
                    <>
                        <p className="mb-3 text-xs text-navy/50">Serving <b className="text-navy">{areaName}</b> · tap + to add multiple services</p>

                        {/* Combine & Save nudge — one home visit for all services */}
                        {cart.length >= 2 && (
                            <div data-testid="combine-save-banner" className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3.5">
                                <div className="rounded-xl bg-emerald-500 p-2 text-white"><Users size={18} /></div>
                                <div className="flex-1">
                                    <p className="font-heading text-sm font-extrabold text-emerald-800">Save time with one home visit</p>
                                    <p className="text-xs text-emerald-700/90">We'll send a professional to handle all {cart.length} services in a single slot{discount > 0 ? ` — and you save ${rupee(discount)}` : ""}.</p>
                                </div>
                            </div>
                        )}

                        <h2 className="mb-2 font-heading text-base font-bold text-navy">Available now</h2>
                        <div className="space-y-2.5">
                            {available.map((s) => {
                                return (
                                    <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-3">
                                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl"><img src={s.image_url} alt={s.name} className="h-full w-full object-cover" /></div>
                                        <div className="flex-1"><h3 className="font-heading text-sm font-bold text-navy">{s.name.replace(" Visit", "")}</h3><p className="text-xs text-orange-dark">{s.price_label}</p></div>
                                        {inCart(s.id) ? <button data-testid={`edit-service-${s.category}`} onClick={() => setPicking(s)} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">Added · Edit</button>
                                            : <button data-testid={`add-service-${s.category}`} onClick={() => setPicking(s)} className="rounded-full bg-orange p-2 text-white active:scale-95"><Plus size={18} /></button>}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Smart recommendations — commonly combined services */}
                        {recommended.length > 0 && (
                            <div data-testid="smart-recommendations" className="mt-5">
                                <div className="mb-2 flex items-center gap-1.5"><Sparkles size={15} className="text-orange" /><h2 className="font-heading text-base font-bold text-navy">Frequently added together</h2></div>
                                <div className="flex gap-2.5 overflow-x-auto pb-1 homiie-scroll">
                                    {recommended.map((s) => (
                                        <div key={s.id} className="w-36 shrink-0 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                                            <div className="h-20 w-full overflow-hidden"><img src={s.image_url} alt={s.name} className="h-full w-full object-cover" /></div>
                                            <div className="p-2.5">
                                                <h3 className="truncate font-heading text-xs font-bold text-navy">{s.name.replace(" Visit", "")}</h3>
                                                <p className="truncate text-[11px] text-orange-dark">{s.price_label}</p>
                                                <button data-testid={`rec-add-${s.category}`} onClick={() => setPicking(s)} className="mt-2 flex w-full items-center justify-center gap-1 rounded-full bg-orange/10 py-1.5 text-xs font-bold text-orange-dark active:scale-95"><Plus size={13} /> Add</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {coming.length > 0 && (
                            <>
                                <h2 className="mb-2 mt-6 font-heading text-base font-bold text-navy">Coming soon in {areaName}</h2>
                                <div className="space-y-2.5">
                                    {coming.map((s) => {
                                        return (
                                            <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-dashed border-navy/15 bg-white/60 p-3" data-testid={`coming-${s.category}`}>
                                                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl opacity-70 grayscale"><img src={s.image_url} alt={s.name} className="h-full w-full object-cover" /></div>
                                                <div className="flex-1"><h3 className="font-heading text-sm font-bold text-navy/70">{s.name.replace(" Visit", "")}</h3><p className="text-xs text-navy/40">Not available in your area yet</p></div>
                                                <button data-testid={`notify-${s.category}`} onClick={() => setDemanding(s)} className="flex items-center gap-1 rounded-full border border-orange/40 px-3 py-1.5 text-xs font-bold text-orange-dark"><BellRing size={13} /> Notify me</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}

                {step === 3 && (
                    <div className="space-y-3.5">
                        {selectedHome && (
                            <div data-testid="booking-home-confirm" className="rounded-2xl border border-orange/30 bg-orange/5 p-3.5">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-orange-dark">Booking for</p>
                                <p className="mt-0.5 font-heading text-sm font-extrabold text-navy">{selectedHome.label} · {selectedHome.home_id}</p>
                                <p className="text-xs text-navy/60">{selectedHome.address_line}{selectedHome.society ? `, ${selectedHome.society}` : ""}</p>
                            </div>
                        )}
                        <Field testid="d-name" label="Name" value={details.name} onChange={(v) => setD("name", v)} />
                        <Field testid="d-phone" label="Mobile number" value={details.phone} onChange={(v) => setD("phone", v)} />
                        {!homeId && <Field testid="d-address" label="Full address" value={details.address_line} onChange={(v) => setD("address_line", v)} ph="House / flat, street, landmark" />}
                        {!homeId && <Field testid="d-society" label="Society / building (optional)" value={details.society} onChange={(v) => setD("society", v)} />}
                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-navy">Preferred date</label>
                            <div className="flex gap-2 overflow-x-auto pb-1 homiie-scroll">
                                {days.map((dd) => <button key={dd.value} onClick={() => setD("preferred_date", dd.value)} className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold ${details.preferred_date === dd.value ? "border-orange bg-orange text-white" : "border-navy/15 bg-white text-navy"}`}>{dd.label}</button>)}
                            </div>
                        </div>
                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-navy">Preferred time (one home visit window)</label>
                            <div className="grid grid-cols-3 gap-2">
                                {TIME_WINDOWS.map((wd) => {
                                    const past = windowPast(wd, details.preferred_date === days[0].value);
                                    return <button key={wd} disabled={past} onClick={() => setD("preferred_window", wd)} data-testid={`time-window-${wd}`} className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${past ? "cursor-not-allowed border-navy/10 bg-navy/5 text-navy/30 line-through" : details.preferred_window === wd ? "border-orange bg-orange text-white" : "border-navy/15 bg-white text-navy"}`}>{wd}</button>;
                                })}
                            </div>
                            {details.preferred_date === days[0].value && TIME_WINDOWS.every((wd) => windowPast(wd, true)) && <p className="mt-1.5 text-xs text-amber-600">No slots left today — please pick another day.</p>}
                        </div>
                        <Field testid="d-referral" label="Referral / Pro code (optional)" value={details.referral_code} onChange={(v) => setD("referral_code", v)} ph="RAMESH1047" />
                        <div className="rounded-2xl border border-navy/10 bg-white p-4">
                            <h3 className="mb-2 font-heading font-bold text-navy">Your service list</h3>
                            {cart.map((c, i) => <div key={i} className="flex justify-between text-sm"><span className="text-navy/70">{c.service.name.replace(" Visit", "")} · {c.problem}</span><span className="font-medium text-navy">{rupee(c.service.base_price)}</span></div>)}
                            {discount > 0 && <div className="mt-1 flex justify-between text-sm text-emerald-600"><span>Multi-service discount</span><span>- {rupee(discount)}</span></div>}
                            <div className="mt-2 flex justify-between border-t border-navy/10 pt-2 font-heading font-extrabold text-navy"><span>Estimated total</span><span>{rupee(subtotal - discount)}</span></div>
                        </div>
                        <label data-testid="consent-checkbox-label" className="flex cursor-pointer items-start gap-3 rounded-2xl border border-navy/15 bg-white p-4">
                            <input
                                data-testid="consent-checkbox"
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                className="mt-0.5 h-5 w-5 shrink-0 accent-orange"
                            />
                            <span className="text-xs leading-relaxed text-navy/70">
                                I have read and agree to HOMIIE{" "}
                                <Link to="/legal/terms-and-conditions" target="_blank" className="font-bold text-orange">Terms &amp; Conditions</Link>,{" "}
                                <Link to="/legal/privacy-policy" target="_blank" className="font-bold text-orange">Privacy Policy</Link>,{" "}
                                <Link to="/legal/payment-policy" target="_blank" className="font-bold text-orange">Payment Policy</Link>,{" "}
                                <Link to="/legal/cancellation-policy" target="_blank" className="font-bold text-orange">Cancellation</Link> &amp;{" "}
                                <Link to="/legal/refund-policy" target="_blank" className="font-bold text-orange">Refund Policy</Link> and{" "}
                                <Link to="/legal/careback-warranty-policy" target="_blank" className="font-bold text-orange">CareBack Policy</Link>.
                            </span>
                        </label>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-4" data-testid="payment-step">
                        <div className="rounded-2xl border border-navy/10 bg-white p-4">
                            <h3 className="mb-1 font-heading font-bold text-navy">Visit / inspection fee</h3>
                            <p className="text-xs text-navy/55">This is a small upfront visit fee to send a verified professional. The actual service cost is estimated on-site and <strong>this visit fee is adjusted against your final bill</strong>.</p>
                            <div className="mt-3 space-y-2 border-t border-navy/10 pt-3">
                                {cart.map((c, i) => (
                                    <div key={i} className="flex items-start justify-between text-sm">
                                        <div><span className="text-navy/80">{c.service.name.replace(" Visit", "")}</span><span data-testid={`pay-problems-${i}`} className="block text-[11px] font-medium text-teal">{c.problem}</span>{c.service.base_price ? <span className="block text-[11px] text-navy/45">Service starts at {rupee(c.service.base_price)}</span> : null}</div>
                                        <div className="text-right"><span className="font-medium text-navy">{rupee(c.service.visit_fee || 0)}</span><span className="block text-[10px] text-navy/40">visit fee · incl. GST {rupee(c.service.visit_fee_gst?.gst || 0)}</span></div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 flex justify-between border-t border-navy/10 pt-2 font-heading text-base font-extrabold text-navy"><span>Total visit fee now</span><span data-testid="merged-visit-fee" className="text-orange">{rupee(visitFeeTotal)}</span></div>
                            <VisitFeeGstNote cart={cart} />
                        </div>
                        <div className="rounded-2xl border border-navy/10 bg-white p-4">
                            <h3 className="mb-2 font-heading font-bold text-navy">Secure payment</h3>
                            <div className="flex items-center gap-3 rounded-xl border-2 border-orange bg-orange/5 p-3">
                                <div className="rounded-lg bg-orange/15 p-2"><Wallet size={18} className="text-orange" /></div>
                                <div className="flex-1"><p className="text-sm font-bold text-navy">UPI · Cards · Netbanking</p><p className="text-xs text-navy/50">Powered by Cashfree — pay securely in the app</p></div>
                                <Check size={18} className="text-orange" />
                            </div>
                        </div>
                        <div data-testid="payment-cancellation-terms" className="rounded-2xl border border-navy/10 bg-white p-4 text-xs text-navy/70">
                            <h3 className="mb-1.5 font-heading text-sm font-bold text-navy">Before you pay</h3>
                            <ul className="list-disc space-y-1 pl-4">
                                <li>Free cancellation with full visit-fee refund until your professional arrives.</li>
                                <li>After the professional arrives, you can still cancel, but the visit fee is not refundable.</li>
                                <li>Once work starts the booking can't be cancelled. Raise a complaint or refund request instead.</li>
                                <li>Labour and materials are quoted on-site. Nothing extra is charged without your approval in the app.</li>
                                <li>Final bill adds a fixed {rupee(cart[0]?.service.platform_fee ?? 9)} platform fee. GST is included in all prices.</li>
                            </ul>
                            <Link to="/legal/cancellation-policy" target="_blank" className="mt-1.5 inline-block font-bold text-orange">Full cancellation policy</Link>
                        </div>
                        <div className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700"><ShieldCheck size={16} className="mt-0.5 shrink-0" /> Your Booking ID is generated only after payment is confirmed. The visit fee is adjusted against your final bill.</div>
                    </div>
                )}
            </div>

            {/* Cart summary bar */}
            {step === 2 && cart.length > 0 && (
                <div className="fixed bottom-20 left-0 right-0 mx-auto max-w-md px-4">
                    <div className="rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(16,42,67,0.15)]">
                        <div className="mb-2 flex items-center justify-between">
                            <span className="font-heading text-sm font-bold text-navy">{cart.length} service{cart.length > 1 ? "s" : ""}{discount > 0 ? ` · ${rupee(discount)} off` : ""}</span>
                            <span className="font-heading font-extrabold text-navy">{rupee(subtotal - discount)}</span>
                        </div>
                        <div className="mb-2 max-h-24 space-y-1 overflow-y-auto homiie-scroll">
                            {cart.map((c, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="text-navy/70">{c.service.name.replace(" Visit", "")} · {c.problem}</span>
                                    <button data-testid={`remove-cart-${i}`} onClick={() => setCart(cart.filter((_, j) => j !== i))} className="text-navy/30 hover:text-red-500"><Trash2 size={14} /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-md border-t border-navy/10 bg-white p-4">
                {step === 1 && <button data-testid="area-continue" disabled={!areaId} onClick={() => setStep(2)} className="w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white disabled:opacity-40">CONTINUE</button>}
                {step === 2 && <button data-testid="to-details-btn" disabled={cart.length === 0} onClick={() => setStep(3)} className="w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white disabled:opacity-40">{cart.length ? `REVIEW ${cart.length} SERVICE${cart.length > 1 ? "S" : ""}` : "ADD A SERVICE"}</button>}
                {step === 3 && <button data-testid="book-all-btn" disabled={busy || !consent} onClick={() => (visitFeeTotal > 0 ? setStep(4) : submit())} className="flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white disabled:opacity-40">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {visitFeeTotal > 0 ? "CONTINUE TO PAYMENT" : "BOOK ALL"}</button>}
                {step === 4 && <button data-testid="pay-visit-fee-btn" disabled={busy} onClick={payAndBook} className="flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white disabled:opacity-40">{busy && <Loader2 className="h-4 w-4 animate-spin" />} PAY {rupee(visitFeeTotal)} SECURELY</button>}
            </div>

            {/* Problem picker */}
            {picking && (
                <Sheet onClose={() => setPicking(null)}>
                    <ProblemPicker service={picking} initial={cart.find((c) => c.service.id === picking.id)?.problems || []} onDone={(ps) => addToCart(picking, ps)} />
                </Sheet>
            )}
            {/* Demand sheet */}
            {demanding && (
                <Sheet onClose={() => setDemanding(null)}>
                    <div className="mb-1 flex items-center gap-2"><BellRing size={20} className="text-orange" /><h3 className="font-heading text-lg font-extrabold text-navy">{demanding.name.replace(" Visit", "")} — Coming Soon</h3></div>
                    <p className="text-sm text-navy/55">Not available in {areaName} yet. Tell us you need it and we'll work to bring it to your area.</p>
                    <p className="mb-2 mt-4 text-sm font-semibold text-navy">When do you need this?</p>
                    <div className="space-y-2">
                        {URGENCY.map((u) => <button key={u} data-testid={`urgency-${u.replace(/[^a-z0-9]/gi, "").toLowerCase()}`} onClick={() => requestDemand(demanding, u)} className="flex w-full items-center gap-2 rounded-xl border border-navy/10 px-4 py-3 text-left text-sm font-medium text-navy hover:border-orange"><Clock size={15} className="text-navy/40" /> {u}</button>)}
                    </div>
                </Sheet>
            )}
        </CustomerShell>
    );
}

function Field({ label, value, onChange, ph, testid }) {
    return <div><label className="mb-1.5 block text-sm font-semibold text-navy">{label}</label><input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none focus:border-orange" /></div>;
}
function Sheet({ children, onClose }) {
    return <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40" onClick={onClose}><div className="mx-auto max-h-[80vh] w-full max-w-md animate-fade-up overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 homiie-scroll" onClick={(e) => e.stopPropagation()}>{children}</div></div>;
}
